import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useIntl } from "react-intl";

import { IconArrowLeft, IconArrowRight } from "@assets/svg";
import MarqueeBackground from "@components/molecules/MarqueeBackground";

import { TestimonialsProps } from "./types";
import * as Styled from "./styles";
import TestimonialsBullet from "./partials/TestimonialsBullet";
import { SLIDE_CHANGE_TIME } from "./consts";

const Testimonials: React.FC<TestimonialsProps> = ({
  items,
  color,
  backgroundText,
  withBackgroundAnimation,
  isAnimationActiveOnMobile
}) => {
  const isSlider = items.length > 1;
  const intl = useIntl();
  const autoplay = useRef(
    Autoplay({ delay: SLIDE_CHANGE_TIME, stopOnInteraction: false })
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mainViewportRef, embla] = useEmblaCarousel(
    {
      active: isSlider,
      skipSnaps: false,
      slidesToScroll: 1
    },
    [autoplay.current]
  );
  const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
    active: isSlider,
    containScroll: "keepSnaps",
    dragFree: true
  });
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(false);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false);
  const [isNavigationVisible, setIsNavigationVisible] =
    useState(false);

  const hasAnonymousTestimonial = useMemo(
    () => items?.some(({ author: { name } }) => !name),
    [items]
  );

  const scrollPrev = useCallback(() => {
    if (embla) {
      embla.scrollPrev();
      if (autoplay && autoplay.current) {
        autoplay.current.reset();
      }
    }
  }, [embla, autoplay]);

  const scrollNext = useCallback(() => {
    if (embla) {
      embla.scrollNext();
      if (autoplay && autoplay.current) {
        autoplay.current.reset();
      }
    }
  }, [embla, autoplay]);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!embla || !emblaThumbs) return;

      embla.scrollTo(index);

      if (autoplay && autoplay.current) {
        autoplay.current.reset();
      }
    },
    [embla, emblaThumbs, autoplay]
  );

  const onSelect = useCallback(() => {
    if (!embla || !emblaThumbs) return;
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnDisabled(!embla.canScrollPrev());
    setNextBtnDisabled(!embla.canScrollNext());
    setIsNavigationVisible(!!embla.slidesNotInView().length);
    emblaThumbs.scrollTo(embla.selectedScrollSnap());
  }, [embla, emblaThumbs, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
  }, [embla, onSelect]);

  return (
    <Styled.MainWrapper>
      <Styled.Wrapper>
        <MarqueeBackground
          text={
            backgroundText ||
            intl.formatMessage({ defaultMessage: "testimonials" })
          }
          rowsNumber={3}
          $withAnimation={withBackgroundAnimation}
          $isActiveOnMobile={isAnimationActiveOnMobile}
          $isIndent={false}
        />
        <Styled.Viewport ref={mainViewportRef}>
          <Styled.Container style={{ color }}>
            {items.map(({ key, slide }) => (
              <Styled.Slide {...{ key }}>{slide}</Styled.Slide>
            ))}
          </Styled.Container>
        </Styled.Viewport>
      </Styled.Wrapper>
      {!hasAnonymousTestimonial ? (
        <Styled.NavigationWrapper>
          {isNavigationVisible && (
            <Styled.ArrowButton
              aria-label="Arrow Button"
              type="button"
              disabled={prevBtnDisabled}
              onClick={scrollPrev}
            >
              <IconArrowLeft />
            </Styled.ArrowButton>
          )}
          <Styled.NavigationViewport ref={thumbViewportRef}>
            <Styled.Bullets>
              {items.map(
                ({ author: { name, ...restAuthor } }, index) => {
                  const isActive = index === selectedIndex;

                  return (
                    <Styled.Bullet key={name} {...{ isActive }}>
                      {isSlider && (
                        <Styled.ProgressBarWrapper>
                          <Styled.ProgressBar {...{ isActive }} />
                        </Styled.ProgressBarWrapper>
                      )}
                      <TestimonialsBullet
                        onClick={
                          isSlider
                            ? () => onThumbClick(index)
                            : undefined
                        }
                        {...{
                          name,
                          isActive,
                          ...restAuthor
                        }}
                      />
                    </Styled.Bullet>
                  );
                }
              )}
            </Styled.Bullets>
          </Styled.NavigationViewport>
          {isNavigationVisible && (
            <Styled.ArrowButton
              aria-label="Arrow Button"
              type="button"
              disabled={nextBtnDisabled}
              onClick={scrollNext}
            >
              <IconArrowRight />
            </Styled.ArrowButton>
          )}
        </Styled.NavigationWrapper>
      ) : null}
    </Styled.MainWrapper>
  );
};

export default Testimonials;
